'use client';

import { Button, ErrorNotice, LoadingFallback } from '@order-up/common';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { getAppCheckHealth } from '../firebase/appCheck';
import { getFirebaseAuth } from '../firebase/config';
import { isAppCheckAuthFailure, useAuth, useSignInWithToken } from '../hooks/useAuth';
import { useUserSessionValidation, useUserSignIn } from '../hooks/useUserAuth';
import { adminApiService } from '../services/adminApi';
import type { SelectionData } from '../types/routes';
import { getAuthServiceUrl } from '../utils/env';
import { errorHandler } from '../utils/errorHandler';
import { createLogger } from '../utils/logger';
import { AppCheckHealthBanner } from './AppCheckHealthBanner';

const logger = createLogger('TokenHandler');

/**
 * How many times the reconcile path retries getCustomToken → signInWithCustomToken
 * before giving up to a terminal error. Success latches implicitly when the
 * Firebase SDK user appears — never a pre-await flag (the old single-shot latch
 * left the app permanently signed-out on the FIRST transient/attestation miss).
 */
const MAX_RECONCILE_ATTEMPTS = 3;
/** Linear backoff between reconcile attempts. */
const RECONCILE_BACKOFF_MS = 1_000;
/**
 * Per-attempt network cap on /auth/custom-token so a single hung request cannot
 * pin the connection for the axios 120s default.
 */
const GET_CUSTOM_TOKEN_TIMEOUT_MS = 10_000;
/**
 * Wall-clock ceiling on the reconcile HOLD, INDEPENDENT of the retry count /
 * per-attempt timeout. This is the only bound that matters: while the HOLD
 * renders the loader, children — including ManagementAccessGate and its OWN
 * escalation timer — are unmounted, so nothing downstream can rescue a stuck
 * hold. Its own named constant (not AuthGuard's 8s SUPPORT_LANDING_GRACE_MS).
 */
const RECONCILE_HOLD_MAX_MS = 12_000;

type Recovery = 'reauth' | 'retry' | 'both';

interface TerminalError {
  error: Error;
  /**
   * - `reauth` — only re-authentication can recover (ghost session / no session
   *   created). Shows "Sign in again", no in-app retry.
   * - `retry`  — service/attestation outage; the session is fine. Shows the
   *   AppCheck banner + in-app "Try again" only (re-auth can't fix a domain
   *   attestation regression and would loop).
   * - `both`   — generic/transient; offer in-app "Try again" AND "Sign in again".
   */
  recovery: Recovery;
}

/**
 * Mint an Error carrying a catalog `code` (+ the original's correlation id) so
 * ErrorNotice/translateError render honest, PII-free copy with a support ref —
 * translateError resolves by `.code` and never echoes a raw error.message.
 */
function toAuthError(original: unknown, code: string): Error {
  const err = new Error(code) as Error & { code?: string; correlationId?: unknown };
  err.code = code;
  const corr = (original as { correlationId?: unknown } | null)?.correlationId;
  if (corr != null) err.correlationId = corr;
  return err;
}

interface TokenHandlerProps {
  children: React.ReactNode;
  authServiceUrl?: string;
  onComplete?: (data: SelectionData) => void;
  loadingComponent?: React.ComponentType;
}

/**
 * Handles Firebase token processing from auth-service redirects and reconciles
 * the Firebase client SDK with an existing server session.
 *
 * This component:
 * 1. Processes a fresh `?token=` handoff → creates the server session and signs
 *    the Firebase client SDK in with the returned custom token.
 * 2. On any subsequent load (no `?token=`) where a valid server session exists
 *    but the Firebase client SDK is signed out, reconciles the SDK via the
 *    existing `POST /auth/custom-token` recovery — now a bounded, observable,
 *    wall-clock-capped state machine instead of a swallowed single-shot attempt.
 * 3. HOLDS the loader (never renders session-only children) while a valid
 *    session has no SDK identity — the whole data layer is gated on the SDK uid,
 *    so a session-only render would hang or leak the wrong tenant.
 * 4. Surfaces an actionable, translated ErrorNotice with the correct recovery
 *    CTA when reconciliation cannot succeed — never an infinite skeleton.
 */
export function TokenHandler({
  children,
  authServiceUrl,
  onComplete,
  loadingComponent: LoadingComponent = LoadingFallback,
}: TokenHandlerProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [terminalError, setTerminalError] = useState<TerminalError | null>(null);
  const [reconcileAttempt, setReconcileAttempt] = useState(0);
  // Explicit kick so the ?token= path can hand off to the reconcile effect
  // (resetting hasProcessedRef alone is a ref write and re-runs nothing).
  const [reconcileNonce, setReconcileNonce] = useState(0);
  const hasProcessedRef = useRef(false);
  const reconcilingRef = useRef(false);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handledTimeoutRef = useRef(false);

  // When the URL carries a fresh `?token=` from the auth-service handoff, this
  // component owns session creation — running useUserSessionValidation in
  // parallel just produces a guaranteed 401 because the cookie hasn't been set
  // yet. Computed once at mount: replaceState() does not re-render, and the
  // effects below re-run via setIsProcessing.
  const hasTokenParamAtMount =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('token');

  const queryClient = useQueryClient();
  const { data: auth } = useAuth();
  const firebaseUser = auth?.firebaseUser ?? null;
  const signInMutation = useUserSignIn();
  const signInWithTokenMutation = useSignInWithToken();
  const sessionQuery = useUserSessionValidation(!hasTokenParamAtMount);

  // useMutation returns a FRESH object every render, so listing the mutation
  // object in the reconcile effect's deps would re-run that effect on every
  // render — including the setIsProcessing(false) commit in `finally` — which
  // re-fires the attempt immediately and collapses the spaced backoff into a
  // getCustomToken storm during the exact outage this fix absorbs. Read it via
  // a latest-value ref so the effect depends only on meaningful state.
  const signInWithTokenMutationRef = useRef(signInWithTokenMutation);
  signInWithTokenMutationRef.current = signInWithTokenMutation;

  // ── Effect 1: fresh `?token=` handoff ──────────────────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token && !isProcessing && !hasProcessedRef.current) {
      logger.debug('Processing Firebase token from auth service');
      try {
        performance.mark('token-handoff-start');
      } catch {
        // Never break the auth flow on a perf-table issue.
      }
      setIsProcessing(true);
      hasProcessedRef.current = true;

      const rememberMe = urlParams.get('rememberMe') === 'true';

      // Clean URL immediately.
      urlParams.delete('token');
      urlParams.delete('authComplete');
      urlParams.delete('rememberMe');
      const newUrl =
        window.location.pathname +
        (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);

      // Tracks whether the session cookie was actually created, so ghost
      // teardown / re-auth classification only fires when a cookie exists.
      let sessionCreated = false;

      signInMutation
        .mutateAsync({ idToken: token, rememberMe })
        .then(async (result) => {
          sessionCreated = true;
          logger.info('Server session created');

          if (result.customToken) {
            logger.debug('Syncing Firebase client SDK');
            await signInWithTokenMutation.mutateAsync(result.customToken);
          }

          logger.info('Authentication complete');
          try {
            performance.mark('token-handoff-complete');
          } catch {
            // ignore
          }
          setIsProcessing(false);

          // If the SDK is still signed out (server returned no customToken, or
          // the SDK sign-in silently didn't stick), hand off to the reconcile
          // effect rather than leave a session-cookie-without-SDK state that
          // hangs downstream — closes the silent method:'session' leak.
          if (!getFirebaseAuth().currentUser) {
            hasProcessedRef.current = false;
            setReconcileNonce((n) => n + 1);
          }

          if (onComplete) {
            onComplete({ token, expiresAt: result.expiresAt });
          }
        })
        .catch((err) => {
          logger.error('Authentication failed', err);
          setIsProcessing(false);
          if (!sessionCreated) {
            // No session cookie was created → re-auth is the only recovery.
            errorHandler.handleError(err, {
              component: 'TokenHandler',
              operation: 'tokenSignIn',
            });
            setTerminalError({
              error: toAuthError(err, 'AUTHENTICATION_ERROR'),
              recovery: 'reauth',
            });
          } else {
            // Cookie exists but the SDK sign-in failed → funnel into the
            // reconcile effect (it classifies App Check vs ghost vs generic,
            // retries, and surfaces).
            hasProcessedRef.current = false;
            setReconcileNonce((n) => n + 1);
          }
        });
    }
  }, [signInMutation, signInWithTokenMutation, onComplete, isProcessing]);

  // ── Effect 2: reconcile the SDK with an existing server session ────────────
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // `reconcilingRef` (not isProcessing) is the in-flight guard. isProcessing
    // is deliberately NOT a dependency: setIsProcessing(false) in `finally`
    // must not re-run this effect, or its cleanup would cancel the pending
    // backoff timer and re-fire the same attempt in a tight loop without ever
    // incrementing reconcileAttempt. Re-entry is only driven by reconcileAttempt
    // (the backoff timer), a new session, or firebaseUser changing.
    if (hasProcessedRef.current || reconcilingRef.current) return;
    if (terminalError) return;
    if (reconcileAttempt >= MAX_RECONCILE_ATTEMPTS) return;

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('token')) return; // Effect 1 owns the ?token= path.

    if (!sessionQuery.data) return; // No server session → nothing to reconcile.
    // Already signed in — check both the reactive cache (what AuthGuard reads)
    // AND the imperative SDK (immediate after a just-completed sign-in, before
    // the onAuthStateChanged cache write lands) so a fresh success can't loop.
    if (firebaseUser || getFirebaseAuth().currentUser) return;

    reconcilingRef.current = true;
    setIsProcessing(true);
    logger.debug('Reconciling Firebase client SDK with server session', {
      attempt: reconcileAttempt,
    });

    let backoffTimer: ReturnType<typeof setTimeout> | null = null;

    adminApiService
      .getCustomToken({ timeoutMs: GET_CUSTOM_TOKEN_TIMEOUT_MS })
      .then(async (customToken) => {
        // Re-check just before signing in: another path may have changed auth
        // state while /custom-token was in flight.
        if (getFirebaseAuth().currentUser) {
          logger.debug('Firebase SDK already signed in mid-reconciliation, skipping');
          setTerminalError(null);
          return;
        }
        await signInWithTokenMutationRef.current.mutateAsync(customToken);
        logger.info('Firebase client SDK reconciled from server session');
        setTerminalError(null); // success latches when firebaseUser flips non-null
      })
      .catch(async (err) => {
        logger.error('Failed to reconcile Firebase client SDK from session', err);

        const code = (err && (err.code || err?.cause?.code)) as string | undefined;
        const message = err instanceof Error ? err.message : String(err ?? '');

        // Ghost session: the cookie's uid points to a deleted/invalid Firebase
        // user (/custom-token mints a token but signInWithCustomToken rejects).
        // The cookie must go so AuthGuard redirects to the auth app.
        const isGhostSession =
          code === 'auth/user-not-found' ||
          code === 'auth/invalid-custom-token' ||
          code === 'auth/user-disabled' ||
          message.includes('auth/user-not-found') ||
          message.includes('auth/invalid-custom-token') ||
          message.includes('auth/user-disabled');

        if (isGhostSession) {
          logger.warn('Ghost session detected, clearing server session', { code, message });
          try {
            await adminApiService.signOut();
          } catch (signOutErr) {
            logger.warn('Failed to clear ghost session cookie', {
              error: signOutErr instanceof Error ? signOutErr.message : String(signOutErr),
            });
          }
          queryClient.clear();
          errorHandler.handleError(err, {
            component: 'TokenHandler',
            operation: 'reconcileGhostSession',
          });
          setTerminalError({
            error: toAuthError(err, 'AUTH_SESSION_INVALID'),
            recovery: 'reauth',
          });
          return;
        }

        errorHandler.handleError(err, {
          component: 'TokenHandler',
          operation: 'reconcileCustomToken',
        });

        // App Check / attestation failure: keep the session, surface a service
        // notice, offer in-app retry only. Widened to any failure during a
        // degraded attestation window (a network-shaped throw before the auth
        // call still classifies as service, not credential).
        if (
          isAppCheckAuthFailure(err) ||
          getAppCheckHealth() === 'degraded' ||
          getAppCheckHealth() === 'refused'
        ) {
          setTerminalError({
            error: err instanceof Error ? err : new Error(message),
            recovery: 'retry',
          });
          return;
        }

        // Generic/network: retry with backoff up to MAX, then terminal.
        if (reconcileAttempt + 1 < MAX_RECONCILE_ATTEMPTS) {
          backoffTimer = setTimeout(() => {
            setReconcileAttempt((n) => n + 1); // the ONLY incrementer
          }, RECONCILE_BACKOFF_MS);
        } else {
          setTerminalError({
            error: toAuthError(err, 'AUTH_FIREBASE_RECONCILE_FAILED'),
            recovery: 'both',
          });
        }
      })
      .finally(() => {
        reconcilingRef.current = false;
        setIsProcessing(false);
      });

    return () => {
      if (backoffTimer) clearTimeout(backoffTimer);
    };
  }, [
    sessionQuery.data,
    firebaseUser,
    reconcileAttempt,
    reconcileNonce,
    terminalError,
    queryClient,
  ]);

  // ── Effect 3: wall-clock ceiling on the HOLD ───────────────────────────────
  // `holding` stays a stable `true` for the whole reconcile (isProcessing may
  // toggle per attempt, but session-present + SDK-absent does not), so this
  // effect arms the timer ONCE and does not reset it on intermediate renders.
  // Gated on a present server session only — NOT isProcessing. During a fresh
  // ?token= handoff sessionQuery is disabled (data undefined) so `holding` is
  // false and the 12s ceiling does NOT cap the initial /auth/signin network
  // call (a slow-but-succeeding handoff must not flash a terminal). The
  // reconcile path always has sessionQuery.data present, so the 12s cap applies
  // there — which is the path that could otherwise hang.
  const holding = !firebaseUser && !terminalError && Boolean(sessionQuery.data);
  useEffect(() => {
    if (!holding) {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
      return;
    }
    if (holdTimerRef.current || handledTimeoutRef.current) return;
    holdTimerRef.current = setTimeout(() => {
      holdTimerRef.current = null;
      if (getFirebaseAuth().currentUser) return; // recovered in the meantime
      handledTimeoutRef.current = true;
      const err = new Error('Firebase SDK reconcile timed out');
      errorHandler.handleError(err, {
        component: 'TokenHandler',
        operation: 'reconcileTimeout',
      });
      setTerminalError({
        error: toAuthError(err, 'AUTH_FIREBASE_RECONCILE_FAILED'),
        recovery: 'both',
      });
    }, RECONCILE_HOLD_MAX_MS);
  }, [holding]);

  // Clear the wall-clock timer on unmount.
  useEffect(() => {
    return () => {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
    };
  }, []);

  const retryReconcile = () => {
    if (reconcilingRef.current) return; // concurrency-safe: no parallel flights
    handledTimeoutRef.current = false;
    setTerminalError(null);
    setReconcileAttempt(0);
  };

  const redirectToAuth = () => {
    const url = authServiceUrl || getAuthServiceUrl() || '/auth';
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `${url}?returnUrl=${returnUrl}`;
  };

  // Show loading while processing a token or an in-flight reconcile attempt.
  if (isProcessing) {
    return <LoadingComponent />;
  }

  // Terminal error surface — translated, PII-free, with the correct recovery.
  if (terminalError) {
    const { error, recovery } = terminalError;
    return (
      <div className="min-h-svh bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4">
          {recovery === 'retry' ? <AppCheckHealthBanner /> : null}
          <ErrorNotice
            error={error}
            onRetry={recovery === 'reauth' ? undefined : retryReconcile}
            retryBusy={reconcilingRef.current}
          />
          {recovery === 'reauth' || recovery === 'both' ? (
            <Button type="button" onClick={redirectToAuth} className="w-full">
              Sign in again
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  // HOLD: a valid server session with the SDK signed out and not mid-`?token=`.
  // Never render session-only children into a data layer gated on the SDK uid.
  if (Boolean(sessionQuery.data) && !firebaseUser && !hasProcessedRef.current) {
    return <LoadingComponent />;
  }

  return <>{children}</>;
}
